import { yupResolver } from '@hookform/resolvers/yup';
import type { AxiosError } from 'axios';
import { useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useAuthModal } from '@features/common/protected-auth-route/model.ts';
import { useAppDispatch, useAuthToken, useCookies, usePlatformSettings } from '@shared/hooks';
import { useErrorHandler } from '@shared/hooks/use-error-handler.ts';
import { authService } from 'library/api/authService';
import { closeAuthModal } from 'library/redux/modal';
import { useAuth } from '../../AuthContext';
import { loginSchema as schema } from './lib';

interface FormValues {
  phonenumber: string;
  password: string;
}

export const useLogin = () => {
  const authContext = useAuth();
  const { is_demo } = usePlatformSettings();
  const { openSuccessAuthModal } = useAuthModal();
  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    setError,
    clearErrors,
  } = useForm<FormValues>({
    resolver: yupResolver<FormValues>(schema),
    defaultValues: is_demo
      ? {
          phonenumber: '+79000000003',
          password: 'Govi1234',
        }
      : undefined,
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);

  const { setToken } = useAuthToken();
  const { removeCookie } = useCookies();

  const dispatch = useAppDispatch();
  const { handleError } = useErrorHandler();

  const login = async (data: FormValues) => {
    setIsLoading(true);
    try {
      removeCookie('csrftoken');
      removeCookie('sessionid');
      const response = await authService.login({
        ...data,
        phonenumber: `+${data.phonenumber.replace(/\+/, '')}`,
      });
      if (!response.data) {
        throw response;
      }

      setToken(response.data.key);
      dispatch(closeAuthModal());
      openSuccessAuthModal({ title: 'Авторизация успешна' });
    } catch (error) {
      handleError(error);

      authContext.dispatch({ type: 'phone', payload: data.phonenumber });
      const err = error as AxiosError;
      if (err.response?.status === 400) {
        setError('root', {
          type: 'manual',
          message: `Не удалось авторизоваться с текущими данными`,
        });
      } else {
        window.location.reload();
      }
    }
    setIsLoading(false);
  };

  const phoneNumberField = useWatch({ name: 'phonenumber', control });
  const password = useWatch({ name: 'password', control });
  const isFieldsNotEmpty = phoneNumberField && password;

  return {
    register,
    control,
    Controller,
    isLoading,
    handleSubmit,
    login,
    errors,
    showPassword,
    setShowPassword,
    isFieldsNotEmpty,
    clearErrors,
  };
};
